import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Select from "react-select";
import axiosInstance from "../utils/Api";
import { selectStorageLocation } from "../store/authSlice";
import ScreenShell, { GRADIENTS } from "../components/ScreenShell";
import BottomSheet from "../components/BottomSheet";
import useToast from "../hooks/useToast";
import { formatQuantity, formatUnit, getErrorMessage, isValidQuantityInput } from "../utils/format";

// ---- API endpoints used on this screen -------------------------------
// GET  storage-location/getCurrentStock
//        -> data.data.storageLocations[] = { _id, name, city, state,
//             stockInfo: [{ rawMaterial: { _id, name, unit }, currentStock }] }
// POST stock-transactions/debitDamageStockFromCentral
//        body: { storageLocation, remark, centralStockTransferData: [{ rawMaterialId, transferQuantity }] }
// -----------------------------------------------------------------------
// The storage location is the one assigned to the logged-in staff (Redux), so
// there is no picker: it is shown read-only in the header and sent in the payload.

const MIN_REMARK_LENGTH = 50;

const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: 48,
        borderRadius: 14,
        background: "#F9FCFD",
        borderColor: state.isFocused ? "#00A4E3" : "#DCEAF1",
        boxShadow: state.isFocused ? "0 0 0 2px rgba(0,164,227,0.25)" : "none",
        ":hover": { borderColor: "#00A4E3" },
    }),
    valueContainer: (base) => ({ ...base, padding: "2px 12px" }),
    input: (base) => ({ ...base, fontSize: 16 }), // 16px stops iOS zoom-on-focus
    placeholder: (base) => ({ ...base, color: "#8A9BA8", fontSize: 14 }),
    singleValue: (base) => ({ ...base, color: "#08213D", fontWeight: 600, fontSize: 14 }),
    menu: (base) => ({ ...base, borderRadius: 14, overflow: "hidden", border: "1px solid #DCEAF1" }),
    menuPortal: (base) => ({ ...base, zIndex: 300 }),
    option: (base, state) => ({
        ...base,
        padding: "10px 14px",
        fontSize: 14,
        cursor: state.isDisabled ? "not-allowed" : "pointer",
        opacity: state.isDisabled ? 0.45 : 1,
        background: state.isSelected ? "#04698F" : state.isFocused ? "#E9F4F9" : "#fff",
        color: state.isSelected ? "#fff" : "#08213D",
    }),
};

// The "can't exceed stock" cap only applies while there is positive stock.
// Materials at zero / negative stock can still be selected and recorded.
const exceedsStock = (qty, currentStock) => currentStock > 0 && qty > currentStock;

const getItemError = (item) => {
    const qty = Number(item.qty);
    if (item.qty === "" || Number.isNaN(qty) || qty <= 0) return "Enter a quantity greater than 0";
    if (exceedsStock(qty, item.currentStock)) {
        return `Only ${formatQuantity(item.currentStock)} ${formatUnit(item.unit)} in stock`;
    }
    return "";
};

const DamageEntry = () => {
    const storageLocation = useSelector(selectStorageLocation); // { _id, name, state, city, address }
    const storageLocationId = storageLocation?._id;
    const { toast, showToast, dismissToast } = useToast();

    // Stock available at the user's location
    const [stockRows, setStockRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");

    // "Add material" mini-form
    const [selected, setSelected] = useState(null);
    const [qty, setQty] = useState("");
    const [addError, setAddError] = useState("");

    // The entry being built
    const [items, setItems] = useState([]); // { rawMaterialId, name, unit, currentStock, qty }
    const [remark, setRemark] = useState("");
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const fetchStock = useCallback(async () => {
        if (!storageLocationId) {
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            setLoadError("");
            const response = await axiosInstance.get("storage-location/getCurrentStock");
            const locations = response?.data?.data?.storageLocations || [];
            // Match strictly on the user's own location, even if the API returns several.
            const mine = locations.find((location) => location._id === storageLocationId);
            setStockRows(Array.isArray(mine?.stockInfo) ? mine.stockInfo : []);
        } catch (err) {
            console.log("Damage entry - fetch stock error:", err?.response);
            setLoadError(getErrorMessage(err, "Failed to fetch stock for your storage location."));
        } finally {
            setLoading(false);
        }
    }, [storageLocationId]);

    useEffect(() => {
        fetchStock();
    }, [fetchStock]);

    // Materials not yet added, with their current stock shown in the dropdown
    const materialOptions = useMemo(() => {
        const added = new Set(items.map((item) => item.rawMaterialId));
        return stockRows
            .filter((row) => row?.rawMaterial?._id && !added.has(row.rawMaterial._id))
            .map((row) => ({
                value: row.rawMaterial._id,
                label: row.rawMaterial.name,
                unit: row.rawMaterial.unit,
                currentStock: Number(row.currentStock || 0),
            }));
    }, [stockRows, items]);

    const itemErrors = items.map(getItemError);
    const remarkLength = remark.trim().length;
    const remarkValid = remarkLength >= MIN_REMARK_LENGTH;
    const canReview =
        !!storageLocationId && items.length > 0 && itemErrors.every((e) => !e) && remarkValid;

    // ---- Add-material handlers ----
    const handleAdd = () => {
        const quantity = Number(qty);
        if (!selected) return setAddError("Select a raw material first.");
        if (!qty || Number.isNaN(quantity) || quantity <= 0) return setAddError("Enter a quantity greater than 0.");
        if (exceedsStock(quantity, selected.currentStock)) {
            return setAddError(`Only ${formatQuantity(selected.currentStock)} ${formatUnit(selected.unit)} available.`);
        }

        setItems((previous) => [
            ...previous,
            {
                rawMaterialId: selected.value,
                name: selected.label,
                unit: selected.unit,
                currentStock: selected.currentStock,
                qty,
            },
        ]);
        setSelected(null);
        setQty("");
        setAddError("");
    };

    const handleItemQty = (rawMaterialId, value) => {
        if (!isValidQuantityInput(value)) return;
        setItems((previous) =>
            previous.map((item) => (item.rawMaterialId === rawMaterialId ? { ...item, qty: value } : item))
        );
    };

    const handleRemove = (rawMaterialId) =>
        setItems((previous) => previous.filter((item) => item.rawMaterialId !== rawMaterialId));

    // ---- Submit ----
    const handleSubmit = async () => {
        if (!canReview || submitting) return;
        try {
            setSubmitting(true);
            const payload = {
                storageLocation: storageLocationId,
                remark: remark.trim(),
                centralStockTransferData: items.map((item) => ({
                    rawMaterialId: item.rawMaterialId,
                    transferQuantity: Number(item.qty),
                })),
            };

            const response = await axiosInstance.post("stock-transactions/debitDamageStockFromCentral", payload);

            if (response?.data?.status === true) {
                showToast("success", "Damage entry submitted successfully.");
                setConfirmOpen(false);
                setItems([]);
                setRemark("");
                setSelected(null);
                setQty("");
                fetchStock(); // refresh so the dropdown shows the reduced stock
            } else {
                showToast("error", response?.data?.message || "Could not submit the damage entry.");
            }
        } catch (err) {
            console.log("Damage entry - submit error:", err?.response);
            setConfirmOpen(false);
            showToast("error", getErrorMessage(err, "Could not submit the damage entry."));
        } finally {
            setSubmitting(false);
        }
    };

    const footer = (
        <div className="flex items-center gap-3">
            <div className="min-w-0">
                <p className="xl-mono text-[9px] font-medium uppercase tracking-wide text-[#5B6B7C]">Items</p>
                <p className="xl-display text-[18px] font-bold text-[#08213D] leading-tight">{items.length}</p>
            </div>
            <button
                type="button"
                onClick={() => setConfirmOpen(true)}
                disabled={!canReview}
                className={`flex-1 h-12 rounded-[16px] text-[14px] font-bold transition active:scale-[0.98] ${
                    canReview
                        ? "bg-[#D92D20] text-white shadow-[0_10px_22px_rgba(217,45,32,0.3)]"
                        : "bg-[#F3E3E2] text-[#C8A5A2] cursor-not-allowed"
                }`}
            >
                Review &amp; submit
            </button>
        </div>
    );

    return (
        <ScreenShell
            title="Damage entry"
            subtitle="Record damaged raw materials"
            gradient={GRADIENTS.danger}
            toast={toast}
            onDismissToast={dismissToast}
            footer={storageLocationId && !loading && !loadError ? footer : null}
        >
            <div className="px-5 pt-5 pb-8 space-y-5">
                {!storageLocationId ? (
                    <div className="rounded-[20px] bg-white border border-[#DCEAF1] px-5 py-8 text-center">
                        <p className="xl-display text-[16px] font-bold text-[#08213D]">No storage location assigned</p>
                        <p className="text-[12px] font-medium text-[#5B6B7C] mt-1">
                            Ask your admin to assign a storage location to your account.
                        </p>
                    </div>
                ) : loading ? (
                    <div className="space-y-3" aria-busy="true">
                        <div className="skeleton h-[188px] rounded-[20px]" />
                        <div className="skeleton h-24 rounded-[20px]" />
                    </div>
                ) : loadError ? (
                    <div className="rounded-[20px] bg-[#FEF1F0] border border-[#F8C9C5] px-5 py-6 text-center">
                        <p className="text-[13px] font-semibold text-[#B42318]">{loadError}</p>
                        <button
                            type="button"
                            onClick={fetchStock}
                            className="mt-3 h-10 px-5 rounded-[12px] bg-white border border-[#F8C9C5] text-[12px] font-bold text-[#B42318] active:scale-95 transition"
                        >
                            Try again
                        </button>
                    </div>
                ) : (
                    <>
                        {/* ---- Add material ---- */}
                        <div className="rounded-[20px] bg-white border border-[#DCEAF1] p-4 shadow-[0_8px_24px_rgba(4,33,63,0.05)]">
                            <p className="xl-mono text-[9px] font-medium uppercase tracking-wide text-[#5B6B7C] mb-3">
                                Add damaged material
                            </p>

                            <label className="block text-[12px] font-bold text-[#08213D] mb-1.5">Raw material</label>
                            <Select
                                options={materialOptions}
                                value={selected}
                                onChange={(option) => {
                                    setSelected(option);
                                    setQty("");
                                    setAddError("");
                                }}
                                isClearable
                                isSearchable
                                placeholder={materialOptions.length ? "Search raw material…" : "No more materials available"}
                                noOptionsMessage={() => "No matching raw material"}
                                styles={selectStyles}
                                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                menuPlacement="auto"
                                formatOptionLabel={(option, { context }) =>
                                    context === "menu" ? (
                                        <div className="flex items-center justify-between gap-3">
                                            <span className="font-semibold truncate">{option.label}</span>
                                            <span className="shrink-0 text-[11px] font-semibold opacity-70">
                                                {formatQuantity(option.currentStock)} {formatUnit(option.unit)}
                                            </span>
                                        </div>
                                    ) : (
                                        option.label
                                    )
                                }
                            />

                            <div className="grid grid-cols-2 gap-3 mt-3">
                                <div>
                                    <label className="block text-[12px] font-bold text-[#08213D] mb-1.5">In stock</label>
                                    <div className="h-12 rounded-[14px] bg-[#F1FAFD] border border-[#DCEAF1] px-3 flex items-center text-[14px] font-bold text-[#04698F]">
                                        {selected ? `${formatQuantity(selected.currentStock)} ${formatUnit(selected.unit)}` : "—"}
                                    </div>
                                </div>
                                <div>
                                    <label htmlFor="damage-qty" className="block text-[12px] font-bold text-[#08213D] mb-1.5">
                                        Damaged qty
                                    </label>
                                    <input
                                        id="damage-qty"
                                        type="text"
                                        inputMode="decimal"
                                        autoComplete="off"
                                        value={qty}
                                        disabled={!selected}
                                        placeholder="0"
                                        onChange={(event) => {
                                            if (isValidQuantityInput(event.target.value)) {
                                                setQty(event.target.value);
                                                setAddError("");
                                            }
                                        }}
                                        onKeyDown={(event) => event.key === "Enter" && handleAdd()}
                                        className="w-full h-12 rounded-[14px] border border-[#DCEAF1] bg-[#F9FCFD] px-3 text-[16px] font-bold text-[#08213D] focus:outline-none focus:ring-2 focus:ring-[#00A4E3]/30 focus:border-[#00A4E3] disabled:opacity-50"
                                    />
                                </div>
                            </div>

                            {addError && <p className="text-[12px] font-semibold text-[#D92D20] mt-2">{addError}</p>}

                            <button
                                type="button"
                                onClick={handleAdd}
                                className="mt-4 w-full h-12 rounded-[14px] bg-[#08213D] text-white text-[13px] font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition"
                            >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
                                    <path d="M12 5v14M5 12h14" />
                                </svg>
                                Add to entry
                            </button>
                        </div>

                        {/* ---- Items in this entry ---- */}
                        <div>
                            <div className="flex items-center justify-between mb-2.5 px-1">
                                <p className="xl-mono text-[9px] font-medium uppercase tracking-wide text-[#5B6B7C]">
                                    In this entry
                                </p>
                                {items.length > 0 && (
                                    <span className="rounded-full bg-[#FEECEB] text-[#B42318] text-[10px] font-bold px-2.5 py-0.5">
                                        {items.length} {items.length === 1 ? "item" : "items"}
                                    </span>
                                )}
                            </div>

                            {items.length === 0 ? (
                                <div className="rounded-[20px] border border-dashed border-[#C9DCE5] bg-white/60 px-5 py-7 text-center">
                                    <p className="text-[13px] font-semibold text-[#5B6B7C]">No materials added yet</p>
                                    <p className="text-[11px] font-medium text-[#8A9BA8] mt-0.5">
                                        {stockRows.length === 0
                                            ? "There is no stock at your storage location."
                                            : "Pick a material above and add the damaged quantity."}
                                    </p>
                                </div>
                            ) : (
                                <ul className="space-y-3">
                                    {items.map((item, index) => {
                                        const error = itemErrors[index];
                                        return (
                                            <li
                                                key={item.rawMaterialId}
                                                className={`rounded-[18px] bg-white border p-3.5 ${
                                                    error ? "border-[#F8C9C5]" : "border-[#DCEAF1]"
                                                }`}
                                            >
                                                <div className="flex items-start justify-between gap-3">
                                                    <div className="min-w-0">
                                                        <p className="text-[14px] font-bold text-[#08213D] leading-snug">{item.name}</p>
                                                        <p className="text-[11px] font-medium text-[#5B6B7C] mt-0.5">
                                                            In stock: {formatQuantity(item.currentStock)} {formatUnit(item.unit)}
                                                        </p>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemove(item.rawMaterialId)}
                                                        aria-label={`Remove ${item.name}`}
                                                        className="shrink-0 w-8 h-8 rounded-full bg-[#FEF1F0] text-[#D92D20] flex items-center justify-center active:scale-90 transition"
                                                    >
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6M10 11v6M14 11v6" />
                                                        </svg>
                                                    </button>
                                                </div>

                                                <div className="relative mt-3">
                                                    <input
                                                        type="text"
                                                        inputMode="decimal"
                                                        autoComplete="off"
                                                        aria-label={`Damaged quantity for ${item.name}`}
                                                        value={item.qty}
                                                        onChange={(event) => handleItemQty(item.rawMaterialId, event.target.value)}
                                                        className={`w-full h-11 rounded-[12px] border pl-3 pr-16 text-[16px] font-bold focus:outline-none focus:ring-2 ${
                                                            error
                                                                ? "border-[#F8C9C5] bg-[#FFFAF9] text-[#B42318] focus:ring-[#E5484D]/25"
                                                                : "border-[#DCEAF1] bg-[#F9FCFD] text-[#08213D] focus:ring-[#00A4E3]/30"
                                                        }`}
                                                    />
                                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-bold text-[#5B6B7C]">
                                                        {formatUnit(item.unit)}
                                                    </span>
                                                </div>
                                                {error && <p className="text-[11px] font-semibold text-[#D92D20] mt-1.5">{error}</p>}
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>

                        {/* ---- Remark ---- */}
                        <div className="rounded-[20px] bg-white border border-[#DCEAF1] p-4">
                            <div className="flex items-center justify-between mb-1.5">
                                <label htmlFor="damage-remark" className="text-[12px] font-bold text-[#08213D]">
                                    Remark <span className="text-[#D92D20]">*</span>
                                </label>
                                <span
                                    className={`text-[11px] font-bold ${remarkValid ? "text-[#0A6B58]" : "text-[#8A9BA8]"}`}
                                >
                                    {remarkLength}/{MIN_REMARK_LENGTH}
                                </span>
                            </div>
                            <textarea
                                id="damage-remark"
                                rows={3}
                                value={remark}
                                onChange={(event) => setRemark(event.target.value)}
                                placeholder={`Explain what was damaged and why (at least ${MIN_REMARK_LENGTH} characters)`}
                                className="w-full rounded-[14px] border border-[#DCEAF1] bg-[#F9FCFD] px-3 py-2.5 text-[16px] font-medium text-[#08213D] focus:outline-none focus:ring-2 focus:ring-[#00A4E3]/30 focus:border-[#00A4E3] resize-none"
                            />
                            {remarkLength > 0 && !remarkValid && (
                                <p className="text-[11px] font-semibold text-[#B26A00] mt-1.5">
                                    {MIN_REMARK_LENGTH - remarkLength} more characters needed
                                </p>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* ---- Confirm sheet (stock debits can't be undone from the app) ---- */}
            <BottomSheet
                open={confirmOpen}
                onClose={() => !submitting && setConfirmOpen(false)}
                title="Confirm damage entry"
                subtitle={storageLocation?.name}
                footer={
                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={() => setConfirmOpen(false)}
                            disabled={submitting}
                            className="flex-1 h-12 rounded-[14px] bg-white border border-[#DCEAF1] text-[13px] font-bold text-[#5B6B7C] active:scale-[0.98] transition disabled:opacity-50"
                        >
                            Go back
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={submitting}
                            className="flex-[1.4] h-12 rounded-[14px] bg-[#D92D20] text-white text-[13px] font-bold shadow-[0_10px_22px_rgba(217,45,32,0.3)] active:scale-[0.98] transition disabled:opacity-70 flex items-center justify-center gap-2"
                        >
                            {submitting && <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />}
                            {submitting ? "Submitting…" : "Submit entry"}
                        </button>
                    </div>
                }
            >
                <p className="text-[12px] font-medium text-[#5B6B7C] mb-3">
                    This will reduce stock at your storage location. Please check the quantities.
                </p>
                <ul className="space-y-2">
                    {items.map((item) => (
                        <li
                            key={item.rawMaterialId}
                            className="flex items-center justify-between gap-3 rounded-[14px] bg-white border border-[#DCEAF1] px-3.5 py-3"
                        >
                            <span className="text-[13px] font-semibold text-[#08213D] min-w-0">{item.name}</span>
                            <span className="shrink-0 text-[13px] font-bold text-[#D92D20]">
                                −{formatQuantity(item.qty)} {formatUnit(item.unit)}
                            </span>
                        </li>
                    ))}
                </ul>
                <p className="xl-mono text-[9px] font-medium uppercase tracking-wide text-[#5B6B7C] mt-4 mb-1.5">Remark</p>
                <p className="text-[13px] font-medium text-[#08213D] leading-relaxed break-words">{remark.trim()}</p>
            </BottomSheet>
        </ScreenShell>
    );
};

export default DamageEntry;